import type { RegionNode } from '../api/regions';

/** A flat region row (e.g. an RD's assigned regions off the staff profile). */
export interface FlatRegion {
  id: string;
  name: string;
  parentId?: string | null;
}

/**
 * Build a `RegionNode[]` forest from a flat list. A row nests under its parent
 * only when that parent is also in the list; otherwise it becomes a root. Used
 * to feed RegionTreeSelect from non-tree sources (e.g. an RD's writable regions)
 * so e.g. "Austin" still nests under "Texas" when both are present.
 */
export function buildRegionForest(flat: FlatRegion[]): RegionNode[] {
  const nodes = new Map<string, RegionNode>();
  for (const r of flat) {
    nodes.set(r.id, {
      id: r.id,
      name: r.name,
      code: '',
      parentId: r.parentId ?? null,
      venueCount: 0,
      children: [],
    });
  }
  const roots: RegionNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/** Flatten a region tree to an id → node map. */
export function flattenById(regions: RegionNode[]): Map<string, RegionNode> {
  const map = new Map<string, RegionNode>();
  const walk = (n: RegionNode) => {
    map.set(n.id, n);
    n.children.forEach(walk);
  };
  regions.forEach(walk);
  return map;
}

/** Ids of a region and all its descendants — the set a venue can belong to and
 *  still count as a member of `regionId` (membership rolls up the subtree). */
export function subtreeIds(byId: Map<string, RegionNode>, regionId: string | null): Set<string> {
  const out = new Set<string>();
  const root = regionId ? byId.get(regionId) : undefined;
  if (!root) return out;
  const stack: RegionNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop()!;
    out.add(node.id);
    node.children.forEach((c) => stack.push(c));
  }
  return out;
}

/** A region's ancestor chain as names, root → deepest (e.g. ["California",
 *  "SoCal", "LA County"]) — the regions a venue assigned here belongs to. */
export function regionChainNames(byId: Map<string, RegionNode>, regionId: string | null): string[] {
  const names: string[] = [];
  let node = regionId ? byId.get(regionId) : undefined;
  while (node) {
    names.unshift(node.name);
    node = node.parentId ? byId.get(node.parentId) : undefined;
  }
  return names;
}
